import { useEffect, useRef } from 'react';

export interface ScenarioAutoComputeArgs {
  /**
   * True only when a fresh arrival at the Compare step of a SAVED, hydrated
   * scenario should produce a comparison without the reader pressing anything.
   * The caller assembles this from: on the Compare step, `scenarioId !== 'new'`,
   * the scenario row has hydrated into the draft, and the draft is in a state
   * the API will accept (no stranded cohorts — a compute on those 400s).
   */
  ready: boolean;
  /** A comparison already exists — never clobber it with a second compute. */
  hasResult: boolean;
  /** A compute is already in flight. */
  isComputing: boolean;
  /** Fire the same mutation the Compute button fires. */
  onCompute: () => void;
}

/**
 * Auto-computes the scenario comparison once on arrival at the Compare step.
 *
 * Why this exists: `compareResult` is React state with no persisted or
 * refetched home, so opening a share link (`?step=5`) landed the recipient on
 * the Compare step's *empty* state — "Save your cohorts and assumptions, then
 * compute the comparison" plus a button — rather than on the finished
 * comparison the sender meant to share.
 *
 * Fires at most ONCE per mount, enforced by a ref rather than by the effect's
 * dependency list: the guarding inputs (`hasResult`, `isComputing`) flip during
 * the compute the effect itself started, so a dependency-only guard would be a
 * re-entrancy hazard. A failed compute also does not retry — the caller falls
 * back to the empty state and its Compute button, which is a deliberate manual
 * retry rather than a loop against a failing endpoint.
 *
 * The author walking the wizard through to Compare normally now also triggers
 * this, which is the same work they were about to ask for by hand.
 */
export function useScenarioAutoCompute({ ready, hasResult, isComputing, onCompute }: ScenarioAutoComputeArgs): void {
  const firedRef = useRef(false);
  // Held in a ref so an inline arrow prop can't retrigger the effect.
  const computeRef = useRef(onCompute);
  computeRef.current = onCompute;

  useEffect(() => {
    if (firedRef.current) return;
    if (!ready || hasResult || isComputing) return;
    firedRef.current = true;
    computeRef.current();
  }, [ready, hasResult, isComputing]);
}
